import { Dispatch, MutableRefObject, ReactNode, SetStateAction } from 'react';

import { MerlinCardInfo, TestCases } from 'types/edgeFunctions';

export type TDevSandboxMode = 'full-page' | 'interactive' | 'script-practice' | 'embed';

// Tab names can be dynamic based on framework/CSS mode settings
export type TCodeTabName = 'HTML' | 'CSS' | 'JS' | 'JSX' | 'TS' | 'VUE' | 'SCSS' | 'PostCSS' | 'CSS Modules';

// Editor Settings Types
export type TEditorFramework = 'vanilla' | 'react' | 'vue' | 'typescript';
export type TCSSMode = 'css' | 'scss' | 'postcss' | 'modules';
export type TEditorTheme = 'dark' | 'light' | 'system';
export type TResolvedTheme = 'dark' | 'light';
export type TEditorLayout = 'left-right' | 'right-left' | 'top-bottom' | 'preview-only';

export interface IEditorPackage {
  name: string;
  version: string;
}

export interface IEditorSettings {
  // Framework & Language
  framework: TEditorFramework;
  cssMode: TCSSMode;

  // Layout
  layout: TEditorLayout;

  // Editor Preferences
  fontSize: number;
  tabSize: 2 | 4;
  lineNumbers: boolean;
  wordWrap: boolean;
  theme: TEditorTheme;
  autoFormat: boolean;

  // Packages
  packages: IEditorPackage[];
}

export const DEFAULT_EDITOR_SETTINGS: IEditorSettings = {
  framework: 'vanilla',
  cssMode: 'css',
  layout: 'left-right',
  fontSize: 14,
  tabSize: 2,
  lineNumbers: true,
  wordWrap: true,
  theme: 'dark',
  autoFormat: false,
  packages: [],
};

/**
 * Clamp editor settings to valid values for the given sandbox mode.
 * Practice problems only support JS/TS frameworks and horizontal layouts.
 * Returns a new object only when changes are needed, otherwise the same reference.
 */
export const clampSettingsForMode = (
  settings: IEditorSettings,
  mode?: TDevSandboxMode,
): IEditorSettings => {
  if (mode !== 'script-practice') return settings;

  const patches: Partial<IEditorSettings> = {};

  // Practice problems: only vanilla (JS) and typescript (TS)
  if (settings.framework !== 'vanilla' && settings.framework !== 'typescript') {
    patches.framework = 'vanilla';
  }

  // Practice problems: only horizontal layouts
  if (settings.layout !== 'left-right' && settings.layout !== 'right-left') {
    patches.layout = 'left-right';
  }

  return Object.keys(patches).length > 0 ? { ...settings, ...patches } : settings;
};

// Helper function to get tab labels based on framework and CSS mode
export const getTabLabels = (
  framework: TEditorFramework,
  cssMode: TCSSMode
): TCodeTabName[] => {
  let cssLabel: TCodeTabName = 'CSS';
  if (cssMode === 'scss') cssLabel = 'SCSS';
  else if (cssMode === 'postcss') cssLabel = 'PostCSS';
  else if (cssMode === 'modules') cssLabel = 'CSS Modules';

  let jsLabel: TCodeTabName = 'JS';
  if (framework === 'react') jsLabel = 'JSX';
  else if (framework === 'vue') jsLabel = 'VUE';
  else if (framework === 'typescript') jsLabel = 'TS';

  return ['HTML', cssLabel, jsLabel];
};

export type TDevSandboxDimensions = {
  codeEditorWidth?: number;
  previewWidth?: number;
};

export type TDevSandboxNavigation = {
  leftSide?: ReactNode | null;
  header?: ReactNode | null;
  rightSide?: ReactNode | null;
};

export type TDevSandboxFooter = {
  leftSide?: ReactNode | null;
  header?: ReactNode | null;
  rightSide?: TDevSandboxFooterQuickButton[] | null;
  assetsConfig?: IAssetsDrawerConfig | null;
  hideTools?: boolean;
  showForkHint?: boolean;
};

export type TCodeTabs = {
  html?: string;
  css?: string;
  js?: string;
  tabNames?: TCodeTabName[];
  tabIndex?: number;
};

export type TDevSandboxPaneTab = {
  screenName: string;
  title: string;
  content: ReactNode;
  icon: ReactNode;
};

export type TDevSandboxPaneTabs = {
  tabIndex?: number;
  tabs: TDevSandboxPaneTab[];
};

export type TDevSandboxFooterQuickButton = {
  id: string;
  tooltip: string;
  title: string | ReactNode;
  content: ReactNode;
  icon: ReactNode;
  autoOpen?: boolean | null;
};

// Assets Drawer Types
export type TAssetsDrawerTab =
  | 'design-assets'
  | 'design-system'
  | 'extract-text'
  | 'your-files'
  | 'packages'
  | 'photos'
  | 'icons'
  | 'fonts'
  | 'colors'
  | 'bigdevtools'
  | 'env-variables';

// Matches ProjectCard from edgeFunctions.ts but simplified for Assets Drawer usage
export interface IProjectCard {
  id: string;
  title: string;
  screenshot_url?: string | null;
  type: string;
  status: string;
  // Optional fields from full ProjectCard type
  screen_name?: string;
  description?: string | null;
  difficulty_level?: string;
}

export interface IAssetsDrawerConfig {
  showDesignTab: boolean;
  showExtractTextTab?: boolean;
  showYourFilesTab: boolean;
  showPackagesTab?: boolean;
  showToolsTab?: boolean;
  designScreenshotUrl?: string | null;
  entityType?: TEntityType;
  entityScreenName?: string;
  entityId?: string;
  // For projects with multiple card screenshots
  projectCards?: IProjectCard[];
  // Figma download props (moved from quickButtons)
  isFreeUser?: boolean;
  isDownloadingFigma?: boolean;
  onFigmaDownload?: () => void;
  // Resource links
  githubLink?: string;
  // Packages tab
  editorPackages?: IEditorPackage[];
  onPackagesChange?: (packages: IEditorPackage[]) => void;
}

export type TDevSandboxHandlers = {
  onHtmlChange: Dispatch<SetStateAction<string>>;
  onCssChange: Dispatch<SetStateAction<string>>;
  onJsChange: Dispatch<SetStateAction<string>>;
  onCodeTabIndexChange: Dispatch<SetStateAction<number>>;
  onPaneTabIndexChange: Dispatch<SetStateAction<number>>;
  onCodeFrameUrlChange: () => void;
  onCodeEditorWidthChange: Dispatch<SetStateAction<number>>;
  onPreviewWidthChange: Dispatch<SetStateAction<number>>;
  onCodeFrameChange: Dispatch<SetStateAction<string>>;
  onBufferFrameChange: Dispatch<SetStateAction<string>>;
  onRunTestsClick: (e: React.MouseEvent<HTMLButtonElement>) => Promise<number>;
  onSetOutputs: Dispatch<SetStateAction<unknown[]>>;
};

export type TDevSandboxCallbacks = {
  onCodeFrameUrlChange: (codeFrameUrl: string) => void;
};

export type TDevSandboxFlags = {
  loadCodeFromServer?: boolean;
  isExternalCode?: boolean;
};

export type TIFrameProps = {
  codeFrameUrl?: string;
  bufferFrameUrl?: string;
};

export type TEntityType = 'project' | 'challenge' | 'code-frame' | 'prototype';

export interface IMerlinAutoTrigger {
  message: string;
  mode: string;
  bypassAiLimit?: boolean;
}

export interface IMerlinConfig {
  entityName: string;
  entityType: string;
  entityDescription?: string;
  designScreenshotUrl?: string;
  acceptanceCriteria?: string[];
  taskCards?: string[];
  currentCard?: MerlinCardInfo | null;
  cards?: MerlinCardInfo[];
  projectScreenName?: string;
  userProjectId?: string | null;
  isDemo?: boolean;
  autoOpen?: boolean;
  autoTrigger?: IMerlinAutoTrigger;
  isCustomTemplate?: boolean;
  originalCode?: { html: string; css: string; js: string };
}

export interface IDemoSpellState {
  isSpellActive: boolean;
  isSpellLoading: boolean;
  savedCode: { html: string; css: string; js: string } | null;
  spellIndex: number;
}

export interface IDevSandboxProps {
  id: string;
  // to support preview_url local storage code from production
  oldKey?: string;
  title: string;
  browserUrl?: string;
  // for external project & challenge solutions
  externalCodeUrl?: string;
  mode?: TDevSandboxMode;
  navigation?: TDevSandboxNavigation;
  codeTabs?: TCodeTabs;
  paneTabs?: TDevSandboxPaneTabs;
  footer?: TDevSandboxFooter;
  iframe?: TIFrameProps;
  flags?: TDevSandboxFlags;
  dimensions?: TDevSandboxDimensions;
  testCases?: TestCases;
  callbacks?: TDevSandboxCallbacks;
  // BigDevTools: entity context for bds.api() proxy + env variables
  entityType?: TEntityType;
  entityId?: string;
  // Merlin AI overlay panel configuration
  merlinConfig?: IMerlinConfig;
  // Author's editor settings for solution viewing (overrides user's local settings)
  authorEditorSettings?: Partial<IEditorSettings> | null;
  // Initial layout — applied on mount via editorSettings update
  initialLayout?: TEditorLayout;
}

export interface IDevSandboxState {
  practiceProblemChecks: {
    hasRun: boolean;
    outputs: unknown[];
    passedTestsCount: number;
    testError: string | null;
  };
}

export type TEditorSettingsHandlers = {
  onEditorSettingsChange: (partial: Partial<IEditorSettings>) => void;
  onEditorSettingsReset: () => void;
};

export interface ICodePreviewContextData extends IDevSandboxProps {
  mode: NonNullable<IDevSandboxProps['mode']>;
  navigation: NonNullable<IDevSandboxProps['navigation']>;
  codeTabs: NonNullable<IDevSandboxProps['codeTabs']>;
  paneTabs: NonNullable<IDevSandboxProps['paneTabs']>;
  iframe: NonNullable<IDevSandboxProps['iframe']>;
  footer: NonNullable<IDevSandboxProps['footer']>;
  flags: NonNullable<IDevSandboxProps['flags']>;
  dimensions: NonNullable<IDevSandboxProps['dimensions']>;
  testCases: NonNullable<IDevSandboxProps['testCases']>;
  handlers: TDevSandboxHandlers & TEditorSettingsHandlers;
  callbacks: NonNullable<IDevSandboxProps['callbacks']>;
  state: IDevSandboxState;
  editorSettings: IEditorSettings;
  resolvedTheme: TResolvedTheme;
  paneRefs?: MutableRefObject<(HTMLDivElement | null)[]>;
  merlin: {
    isOpen: boolean;
    config: IMerlinConfig | null;
    onOpen: () => void;
    onToggle: () => void;
    onClose: () => void;
  };
  demoSpell: {
    state: IDemoSpellState;
    setState: Dispatch<SetStateAction<IDemoSpellState>>;
    cancelRef: MutableRefObject<boolean>;
    timeoutsRef: MutableRefObject<ReturnType<typeof setTimeout>[]>;
    revert: () => void;
  };
}
